import type { CommercialPermitDelivery, CountyFipsEntry } from '@tracttrace/shared';
import { apiUrl } from './apiBase';

const SESSION_KEY = 'tracttrace.admin.secret';

export function readAdminSecret(): string {
  try {
    return sessionStorage.getItem(SESSION_KEY) ?? '';
  } catch {
    return '';
  }
}

export function persistAdminSecret(secret: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, secret);
  } catch {
    // ignore
  }
}

export function clearAdminSecret(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

function adminHeaders(secret: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-admin-secret': secret,
  };
}

export interface SimulateResponse {
  ok: true;
  county: CountyFipsEntry;
  days: number;
  permit_count: number;
  credits_used: number;
  credits_note: string;
  latency_ms: number;
  municipal_raw: unknown[];
  enriched: CommercialPermitDelivery[];
  digest: unknown;
  webhooks: unknown[];
  live_probe?: { ok: boolean; latency_ms: number; error?: string; docId?: string } | null;
}

export async function runCountySimulation(input: {
  secret: string;
  county: string;
  days?: number;
  live?: boolean;
}): Promise<SimulateResponse> {
  const params = new URLSearchParams({
    county: input.county,
    days: String(input.days ?? 7),
  });
  if (input.live) params.set('live', '1');
  const res = await fetch(apiUrl(`/api/v1/simulate?${params.toString()}`), {
    method: 'GET',
    headers: adminHeaders(input.secret),
  });
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
  } & Partial<SimulateResponse>;
  if (!res.ok || body.ok !== true) {
    throw new Error(body.error ?? `Simulate failed (${res.status})`);
  }
  return body as SimulateResponse;
}

export async function fetchAdminHealth(secret: string): Promise<Record<string, unknown>> {
  const res = await fetch(apiUrl('/api/v1/admin/health'), {
    headers: adminHeaders(secret),
  });
  if (!res.ok) throw new Error(`Health failed (${res.status})`);
  return (await res.json()) as Record<string, unknown>;
}

export async function runAdminPipeline(input: {
  secret: string;
  stage: 'ingest' | 'enrich' | 'dispatch' | 'sweep';
  county?: string;
  days?: number;
  sourceUrl?: string;
}): Promise<Record<string, unknown>> {
  const res = await fetch(apiUrl('/api/v1/admin/pipeline'), {
    method: 'POST',
    headers: adminHeaders(input.secret),
    body: JSON.stringify({
      stage: input.stage,
      county: input.county,
      days: input.days,
      sourceUrl: input.sourceUrl,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
  };
  if (!res.ok || body.ok === false) {
    throw new Error(body.error ?? `Pipeline ${input.stage} failed (${res.status})`);
  }
  return body as Record<string, unknown>;
}

export async function verifyAdminGate(secret: string): Promise<boolean> {
  try {
    const res = await fetch(apiUrl('/api/v1/admin/gate'), {
      headers: adminHeaders(secret),
    });
    return res.ok;
  } catch {
    return false;
  }
}
